<script lang="ts" setup>
  import { type FunctionalComponent, ref, toRefs } from 'vue'

  import Checkbox, { type CheckboxProps } from 'primevue/checkbox'

  type Props = Omit<CheckboxProps, 'modelValue' | 'value' | 'inputId'> & {
    label?: string
    labelPos?: 'left' | 'right'
    labelClass?: CssClassType
    icon?: FunctionalComponent
    iconUnchecked?: FunctionalComponent
    iconClass?: CssClassType
    checkboxClass?: CssClassType
    iconUncheckedClass?: CssClassType
    help?: string
  }

  interface Emits {
    change: [event: Event]
    focus: [event: Event]
    blur: [event: Event]
  }

  defineEmits<Emits>()
  const props = withDefaults(defineProps<Props>(), {
    labelPos: 'left',
    defaultValue: undefined,
    help: undefined,
    name: undefined,
    binary: undefined,
    indeterminate: undefined,
    size: undefined,
    invalid: undefined,
    disabled: undefined,
    variant: undefined,
    readonly: undefined,
    required: undefined,
    tabindex: undefined,
    trueValue: undefined,
    falseValue: undefined,
    inputClass: undefined,
    inputStyle: undefined,
    ariaLabelledby: undefined,
    ariaLabel: undefined,
    formControl: undefined,
    dt: undefined,
    pt: undefined,
    ptOptions: undefined,
    unstyled: undefined,
    label: undefined,
    labelClass: undefined,
    icon: undefined,
    iconUnchecked: undefined,
    iconClass: undefined,
    checkboxClass: undefined,
    iconUncheckedClass: undefined
  })
  const value = defineModel<unknown>({ required: true })
  const { help, label, iconClass, iconUncheckedClass, labelClass } = toRefs(props)
  const { icon } = props
  let { iconUnchecked } = props
  const localIconUncheckedClass = ref(iconUncheckedClass.value)
  if (!iconUnchecked && icon) {
    iconUnchecked = icon
    if (!localIconUncheckedClass.value) localIconUncheckedClass.value = iconClass.value
  }

  const hasHelp = computed(() => !!help.value)
  const tooltip = computed(() => {
    if (!hasHelp.value) return undefined

    return {
      value: help.value,
      modifiers: { bottom: true }
    }
  })
</script>

<template>
  <div
    :class="['flex align-items-center']"
    @click.stop="
      () => {
        /*NoOp*/
      }
    ">
    <label
      v-if="label && labelPos == 'left'"
      :for="$id('cb')"
      v-tooltip="tooltip"
      :class="['mr-2 text-sm', { 'cursor-help underline': hasHelp }, labelClass]">
      {{ label }}
    </label>
    <Checkbox
      v-model="value"
      :input-id="$id('cb')"
      v-bind="props"
      @blur="(e: Event) => $emit('blur', e)"
      @change="(e: Event) => $emit('change', e)"
      @focus="(e: Event) => $emit('focus', e)">
      <template #icon="scope: { checked: boolean; class: string }">
        <component
          :is="icon"
          v-if="scope.checked"
          :class="[iconClass ?? '', scope.class]"
          class="size-4" />
        <component
          :is="iconUnchecked"
          v-if="!scope.checked"
          :class="[localIconUncheckedClass ?? '', scope.class]"
          class="size-4" />
      </template>
    </Checkbox>

    <label
      v-if="label && labelPos == 'right'"
      v-tooltip="tooltip"
      :class="[labelClass, { 'cursor-help underline': hasHelp }]"
      :for="$id('cb')"
      class="ml-2 text-sm titrop-label">
      {{ label }}
    </label>
  </div>
</template>
